/**
 * Centralized tooltip definitions for KPI components
 * This file contains all tooltip text to ensure consistency across the application
 */

export const TOOLTIP_DEFINITIONS = {
  // AR KPI Row Tooltips
  ar: {
    claim_ar:
      'Total outstanding insurance accounts receivable amount for all open claims. This represents the total amount owed for services rendered that have not yet been collected.',
    claim_ar_change:
      'Percentage change in AR compared to the end of last month. Positive values indicate increasing AR (more money owed), negative values indicate decreasing AR (better collections).',
    collected_mtd:
      'Total amount of money collected month-to-date. This includes all payments received and posted during the current month.',
    collected_forecasted_change:
      'Projected change in collections based on current month-to-date performance and average daily collection rates from the last 30 and 90 days.',
    claim_ncr_6_months:
      'Net Collection Rate - the percentage of total expected revenue that was actually collected over the last 6 months. Formula: (Amount Collected / Amount Produced) × 100.',
    claim_ncr_change:
      'Change in Net Collection Rate compared to the previous 6-month period. Positive values indicate improving collection efficiency.',
    claim_yield_6_months:
      'Percentage of the expected amount that was actually paid on claims over the last 6 months. This measures how much of the expected amount is being realized.',
    claim_yield_change:
      'Change in Claim Yield compared to the previous 6-month period. Higher yields indicate better reimbursement rates.',
    collection_days:
      'Number of days it would take to collect the current AR balance based on the average daily collection rate from the last 90 days. Lower is better.',
    claim_ar_age:
      'Average age of accounts receivable in days. Lower values indicate faster collections.',
    worked_mtd:
      'Total number of claims worked month-to-date. This includes all claims that have had any work activity performed on them.',
    worked_forecasted_change:
      'Projected change in claims worked based on current month-to-date performance compared to last month.',
    ar_details_nav: 'AR Details',
    collection_details_nav: 'Collection Details',
    ncr_details_nav: 'NCR Details',
    collection_days_details_nav: 'Collection Days details',
    worked_details_nav: 'Worked Details',

    // AR Chart Tooltips
    claim_ar_by_age:
      'Breakdown of accounts receivable by aging buckets. Aging buckets are calculated from date of service. Older AR typically indicates collection challenges and should be prioritized for follow-up.',
    claim_ar_rcm_status:
      'Distribution of AR by RCM workflow status. "Worked" refers to any AR that has been worked, "< 30" are new claims, and "To Be Worked" is AR that has not been worked.',
    claim_ar_responsibility:
      'Breakdown of AR by responsible party (Insurance vs Patient responsibility). This helps identify where collection efforts should be focused.',
    claim_ar_vs_production_mom:
      'Month-over-month comparison of outstanding AR balance against production (Insurance portion only). Helps identify trends in AR accumulation relative to production.',
    claim_collection_vs_production_mom:
      'Month-over-month comparison of actual collections against net production. This shows collection efficiency trends over time.',
    claim_collection_by_age:
      'Breakdown of collections by the age of claims when payment was posted. Shows which age buckets are most successfully collected from.',
    clean_claim_rate:
      'Percentage of claims AR that is submitted without errors or issues that require correction. Clean claims process faster and have better reimbursement rates. Higher percentages indicate better billing accuracy.',
    claim_ar_by_facility:
      'Distribution of accounts receivable across different facilities or offices. This helps identify which locations may need additional collection focus or support.',

    // AR Detail Page Chart Tooltips
    ncr_collection_amount_mom:
      'Month-over-month view of collection amounts with Net Collection Rate (NCR) and Claim Yield percentages. Shows collection efficiency trends over time.',
    collection_days_distribution:
      'Shows the distribution of days it takes to collect on claims by age buckets. Helps identify collection patterns and where process improvements may be needed.',
    worked_claims_performance:
      'Tracks the volume and efficiency of claims work activities. Shows productivity trends and outcomes of claim management efforts.',
    ar_age_distribution_detailed:
      'Detailed breakdown of accounts receivable by aging categories. Provides granular view of AR aging to prioritize collection efforts on older, at-risk balances.',
    clean_claims_trends:
      'Historical trends in clean claim submission rates. Tracks billing accuracy improvements over time and identifies opportunities to reduce claim rejections.',

    // Collection Days Details Page Tooltips
    collection_days_and_speed:
      'Shows collection days (time to collect current AR at current collection rate) alongside 30-day and 90-day collection speed trends. Lower collection days indicate faster AR turnover.',

    // Worked Details Page Tooltips
    claims_worked_mom:
      'Month-over-month trend of claims worked, broken down by AR age buckets. Shows work distribution patterns and helps identify where work is focused on.',
    claims_worked_by_age_pie:
      'Distribution of claims worked over the last 6 months by AR age bucket. Shows which age groups receive the most attention from the RCM staff.',

    // AR Age Details Page Tooltips
    ar_trend_by_age_bucket:
      'Historical trend of accounts receivable broken down by age buckets, with 90+ day AR percentage overlay. Shows aging patterns and identifies if older AR is growing disproportionately.',
    ar_age_mom:
      'Average age of accounts receivable and closed claims over time. Tracks collection efficiency - lower ages indicate faster resolution of outstanding claims.',

    // Clean Claim Details Page Tooltips
    clean_claim_rate_mom:
      'Month-over-month view of total AR, clean claim AR, and clean claim rate percentage. Clean claims process faster and have better reimbursement rates - higher clean rates indicate efficient processing.',
  },

  // Denials KPI Row Tooltips
  denials: {
    dollars_denied:
      'Total dollar amount of benefits that were denied by insurance companies. This includes both partial and full denials.',
    count_denied:
      'Total number of claims that received either partial or full denials from insurance companies.',
    count_full_denied:
      'Number of claims that were completely denied with no payment from the insurance company.',
    denial_rate:
      'Percentage of total expected benefits that were denied. Formula: (Denied Benefits / Total Expected Benefits) × 100.',
    denied_claims_worked:
      'Number of denied claims that have had work performed on them.',
  },

  // Productivity KPI Row Tooltips
  productivity: {
    claims_worked:
      'Total number of claims that have had any work activity (adding Notes, Workflow Statuses, Denial Reasons) performed on them during the selected time period.',
    claims_worked_open: 'Number of open claims that were worked on.',
    dollars_posted_worked:
      'Total dollar amount posted (payments received) from claims that were worked on during the selected period.',
    close_rate_worked:
      'Percentage of worked claims that were successfully closed during the selected period. Formula: (Closed Claims / Total Worked Claims) × 100.',
    work_to_close_days:
      'Average number of days from when work begins on a claim until it is closed. Lower values indicate more efficient claim resolution.',
    work_events_per_day:
      'Average number of work events (claim activities) performed per day during the selected period. This measures team productivity and workflow efficiency.',
  },

  // Productivity Chart Tooltips
  productivity_charts: {
    touch_events_trend:
      'Shows the trend of touch events over time, split between "View Only" events (claims viewed but not worked) and "Worked" events (claims that had actual work performed). This helps identify user engagement patterns and work productivity trends.',
    work_events_breakdown_age:
      'Breakdown of work events by the age bucket of the claims being worked on. Shows which age groups are receiving the most attention.',
    work_events_breakdown_responses:
      'Distribution of work events by the type of response received from insurance companies.',
    work_events_breakdown_expected:
      'Breakdown of work events by the expected dollar value of the claims being worked. Shows whether staff are focusing on high-value or low-value claims, helping optimize resource allocation.',
    work_events_trend:
      'Trend chart showing work events over time with close rate percentage overlay. Combines volume metrics (work events) with efficiency metrics (close rate) to show both productivity and effectiveness trends.',
    claims_by_work_events:
      'Distribution of claims by the number of work events performed on each claim. Shows work intensity patterns - claims requiring multiple touches vs. those resolved quickly.',
  },

  // Denial Chart Tooltips
  denial_charts: {
    denials_trend:
      'Trend chart showing denied claim expected$ over time. Tracks both the volume of dollars denied and the overall denial rate to identify patterns and improvement opportunities.',
    denials_breakdown_age:
      'Breakdown of denied claims by age bucket. Shows which age groups have the highest denial rates.',
    denials_breakdown_status:
      'Distribution of denials by status (Full vs Partial denials). Full denials may require complete resubmission or appeals, while partial denials may only need adjustments or additional documentation.',
    denials_breakdown_ansi:
      'Breakdown of denials by ANSI (American National Standards Institute) reason categories. These standardized codes across multiple payers help identify the most common reasons for denials and target improvement efforts.',
  },
} as const;

// Type-safe tooltip accessor
export type TooltipCategory = keyof typeof TOOLTIP_DEFINITIONS;
export type ARTooltipKey = keyof typeof TOOLTIP_DEFINITIONS.ar;
export type DenialsTooltipKey = keyof typeof TOOLTIP_DEFINITIONS.denials;
export type ProductivityTooltipKey =
  keyof typeof TOOLTIP_DEFINITIONS.productivity;
export type ProductivityChartsTooltipKey =
  keyof typeof TOOLTIP_DEFINITIONS.productivity_charts;
export type DenialChartsTooltipKey =
  keyof typeof TOOLTIP_DEFINITIONS.denial_charts;
